<template>
    <div class="sidebar-container">
        <div
            class="sidebar desktop-only"
            @mouseenter="isExpanded = true"
            @mouseleave="isExpanded = false"
            :class="{ 'is-expanded': isExpanded }"
        >
            <div class="sidebar-content">
                <div class="nav-section logo-section">
                    <div class="logo-wrapper">
                        <router-link to="/">
                            <span class="latex-icon">C</span>
    
                            <Transition name="logo-switch" mode="out-in">
                                <span v-if="!isExpanded" key="symbol" class="chem-m"
                                    >m</span
                                >
                                <span v-else key="name" class="logo-text"
                                    >urie</span
                                >
                            </Transition>
                        </router-link>
                    </div>
                </div>

                <div class="nav-separator"></div>

                <div class="nav-section top-section">
                    <button
                        v-for="item in NAV_ITEMS"
                        :key="item.id"
                        @click="handleNavigate(item)"
                        :class="['nav-item', { active: activeTab === item.id }]"
                        :title="!isExpanded ? item.label : ''"
                    >
                        <div class="icon-wrapper">
                            <component :is="item.icon" :size="24" />
                        </div>
                        <span class="nav-label">{{ item.label }}</span>
                    </button>
                </div>

                <div class="nav-section bottom-section">
                    <button
                        v-for="item in USER_ITEMS"
                        :key="item.id"
                        @click="handleNavigate(item)"
                        :class="['nav-item', { active: activeTab === item.id }]"
                        :title="!isExpanded ? item.label : ''"
                    >
                        <div class="icon-wrapper">
                            <component :is="item.icon" :size="24" />
                        </div>
                        <span class="nav-label">{{ item.label }}</span>
                    </button>

                    <div class="nav-separator"></div>

                    <!-- Guest: Show Register and Login -->
                    <template v-if="isGuest">
                        <button
                            @click="router.push('/register')"
                            class="nav-item register-item"
                            :title="!isExpanded ? 'Register' : ''"
                        >
                            <div class="icon-wrapper">
                                <UserPlus :size="24" />
                            </div>
                            <span class="nav-label">Register</span>
                        </button>
                        <button
                            @click="router.push('/login')"
                            class="nav-item"
                            :title="!isExpanded ? 'Log In' : ''"
                        >
                            <div class="icon-wrapper">
                                <LogIn :size="24" />
                            </div>
                            <span class="nav-label">Log In</span>
                        </button>
                    </template>

                    <!-- Authenticated: Show Logout -->
                    <button
                        v-else
                        @click="handleLogout"
                        class="nav-item logout-item"
                        :title="!isExpanded ? 'Logout' : ''"
                    >
                        <div class="icon-wrapper">
                            <LogOut :size="24" />
                        </div>
                        <span class="nav-label">Logout</span>
                    </button>
                </div>
            </div>
        </div>

        <div class="bottom-nav mobile-only">
            <button
                v-for="item in NAV_ITEMS"
                :key="item.id"
                @click="handleNavigate(item)"
                :class="['bottom-nav-item', { active: activeTab === item.id }]"
            >
                <component :is="item.icon" :size="24" />
                <span>{{ item.label }}</span>
            </button>
            <button
                @click="handleNavigate(profileItem)"
                :class="[
                    'bottom-nav-item',
                    { active: activeTab === 'profile' },
                ]"
            >
                <User :size="24" />
                <span>Profile</span>
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import {
    GraduationCap,
    BarChart3,
    Settings,
    User,
    LogOut,
    UserPlus,
    LogIn,
} from "lucide-vue-next";

const props = defineProps({
    activeTab: { type: String, default: "practice" },
    userName: { type: String, default: "Student" },
    isGuest: { type: Boolean, default: false },
});

const emit = defineEmits(["navigate", "logout"]);
const router = useRouter();
const isExpanded = ref(false);

const NAV_ITEMS = [
    {
        id: "practice",
        label: "Practice",
        icon: GraduationCap,
        path: "/practice",
    },
    {
        id: "analytics",
        label: "Analytics",
        icon: BarChart3,
        path: "/analytics",
    },
];

const USER_ITEMS = [
    { id: "settings", label: "Settings", icon: Settings, path: "/settings" },
    { id: "profile", label: "Profile", icon: User, path: "/profile" },
];

const profileItem = computed(() =>
    USER_ITEMS.find((item) => item.id === "profile")
);

const handleNavigate = (item) => {
    emit("navigate", item.id);
    router.push(item.path);
};

const handleLogout = () => emit("logout");
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Crimson+Pro:wght@600&display=swap");

/* --- Base Sidebar Structure --- */
.sidebar {
    position: fixed;
    left: 0;
    top: 0;
    height: 100vh;
    width: 70px;
    background: var(--bg-primary);
    border-right: 1px solid rgba(var(--accent-rgb), 0.4);
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 1000;
}

.sidebar-hidden {
    transform: translateX(-100%); /* Slide it out of view */
    pointer-events: none; /* Prevent clicking while hidden */
}

.sidebar.is-expanded {
    width: 190px;
}

.sidebar-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 12px 0;
}

/* --- LaTeX Logo & Router Link --- */
.logo-section {
    padding: 0 10px;
}

.logo-wrapper {
    display: flex;
    padding: 5px;
    width: 100%;
    justify-content: center;
    margin-bottom: 4px;
}

/* Resetting the Router Link (The Purple Link Fix) */
.logo-wrapper a {
    text-decoration: none;
    color: var(--text-secondary) !important; /* Force brand color */
    display: flex;
    align-items: baseline;
    width: 100%;
    outline: none;
}

.logo-wrapper a:visited {
    color: var(--text-secondary) !important;
}

.logo-wrapper:hover a,
.logo-wrapper:hover a:visited {
    color: var(--accent) !important;
    transition: ease 0.2s;
}

.latex-icon {
    font-family: "Crimson Pro", serif;
    font-size: 32px;
    font-weight: 600;
    line-height: 1;
    z-index: 2;
}

.chem-m, .logo-text {
    font-family: "Crimson Pro", serif;
    font-weight: 600;
    line-height: 1;
    display: inline-block;
}

.chem-m {
    font-size: 20px; /* Roughly 0.75em of the 26px C */
    margin-left: 1px;
}

.logo-text {
    font-size: 24px;
    margin-left: 0px;
}

/* --- The "Shunt/Scroll" Transition --- */
.logo-switch-enter-active,
.logo-switch-leave-active {
    transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.logo-switch-enter-from {
    opacity: 0;
    transform: translateX(-15px); /* Slide from behind C */
}

.logo-switch-leave-to {
    opacity: 0;
    transform: translateX(15px); /* Slide out to the right */
}

/* --- Navigation Items --- */
.nav-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0 10px;
}

.bottom-section {
    margin-top: auto;
}

.nav-item {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px;
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    color: var(--text-secondary);
    transition: all 0.2s ease;
    white-space: nowrap;
    width: 100%;
}

.icon-wrapper {
    min-width: 24px;
    display: flex;
    justify-content: center;
}

.nav-label {
    opacity: 0;
    font-size: 15px;
    font-weight: 500;
    transition: opacity 0.3s ease;
    color: var(--text-primary);
}

.sidebar.is-expanded .nav-label {
    opacity: 1;
}

.nav-item:hover,
.nav-item.active {
    background: rgba(var(--accent-rgb), 0.2);
    color: var(--text-secondary);
    .nav-label {
        color: var(--text-primary);
    }
}

.nav-separator {
    height: 1px;
    background-color: rgba(var(--accent-rgb), 0.4);
    margin: 8px 0;
    width: 100%;
    width: calc(100% - 20px);
    margin: 8px auto;
}

/* --- Mobile Nav --- */
.bottom-nav {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 70px;
    background: var(--bg-primary);
    display: flex;
    justify-content: space-around;
    align-items: center;
    z-index: 1000;
    border-top: solid 1px rgba(var(--accent-rgb), 0.4);
}

.bottom-nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: var(--text-primary);
    background: none;
    border: none;
    font-size: 12px;
}

.bottom-nav-item.active {
    color: var(--text-secondary);
}

.logout-item {
    color: var(--text-primary);
}

.logout-item:hover {
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
    .nav-label {
        color: var(--text-primary);
    }
}

.register-item {
    color: var(--text-secondary);
}

.register-item:hover {
    background: rgba(var(--accent-rgb), 0.25);
    .nav-label {
        color: var(--text-secondary);
    }
}

@media (max-width: 768px) {
    .desktop-only { display: none; }
    .mobile-only { display: flex; }
}

@media (min-width: 769px) {
    .mobile-only { display: none; }
}
</style>